import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const Supernova = ({ position, scale = 1 }) => {
    const groupRef = useRef();
    const coreRef = useRef();
    const shockwaveRef = useRef();
    const debrisRef = useRef();

    // Debris Particles
    const debrisCount = 100;
    const debris = useMemo(() => {
        const temp = [];
        for (let i = 0; i < debrisCount; i++) {
            const angle = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            const r = 0.5 + Math.random() * 0.5; // Start radius
            
            temp.push({
                x: r * Math.sin(phi) * Math.cos(angle),
                y: r * Math.sin(phi) * Math.sin(angle),
                z: r * Math.cos(phi),
                speed: 0.5 + Math.random() * 0.5,
                size: 0.05 + Math.random() * 0.05
            });
        }
        return temp;
    }, []);

    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        
        // 1. Expanding Core (Pulsing expansion)
        if (coreRef.current) {
            const expansion = 1 + (Math.sin(t * 2) * 0.1) + (Math.random() * 0.05); // Chaos
            coreRef.current.scale.setScalar(expansion);
            coreRef.current.rotation.y += 0.02;
        }

        // 2. Expanding Shockwave
        if (shockwaveRef.current) {
            // Expands and fades repeatedly
            const loopTime = t % 4; // Slower loop
            const waveScale = 1 + loopTime * 0.5; // Slower, less drastic expansion
            shockwaveRef.current.scale.setScalar(waveScale);
            // Manual opacity fade
            shockwaveRef.current.material.opacity = 0.1 - (loopTime * 0.02); // Fades from 0.1 down to 0
            if (shockwaveRef.current.material.opacity < 0) shockwaveRef.current.material.opacity = 0;
        }

        // 3. Debris Field (Orbiting/Expanding)
        if (debrisRef.current) {
            debrisRef.current.rotation.y += 0.005;
        }
    });

    return (
        <group ref={groupRef} position={position} scale={scale}>
            {/* 1. Unstable Core */}
            <mesh ref={coreRef}>
                <sphereGeometry args={[0.5, 32, 32]} />
                <meshStandardMaterial 
                    emissive="#ff5500" 
                    emissiveIntensity={4}
                    color="#ffaa00"
                    roughness={0.4}
                    metalness={0.8}
                    toneMapped={false}
                />
                <pointLight distance={8} intensity={4} color="#ff8800" decay={2} />
            </mesh>

            {/* 2. Shockwave Sphere */}
            <mesh ref={shockwaveRef}>
                <sphereGeometry args={[0.6, 32, 32]} />
                <meshBasicMaterial 
                    color="#ffddaa" // Softer orange
                    transparent 
                    opacity={0.1} // Lower initial opacity
                    side={THREE.BackSide} 
                    blending={THREE.AdditiveBlending}
                />
            </mesh>

            {/* 3. Debris Cloud */}
            <group ref={debrisRef}>
                {debris.map((d, i) => (
                    <mesh key={i} position={[d.x, d.y, d.z]}>
                        <dodecahedronGeometry args={[d.size, 0]} />
                        <meshBasicMaterial color="#ffcc00" transparent opacity={0.6} blending={THREE.AdditiveBlending} />
                    </mesh>
                ))}
            </group>
        </group>
    );
};

export default Supernova;